/**
 * DemoIntroModal · P&L Demo intro modal for the Knowledge Graph page.
 *
 * Byte-verbatim extraction of the Demo intro modal that used to live at
 * lines 1801-1872 of /app/frontend/src/pages/KnowledgeGraphPage.jsx. Zero
 * visual change · every data-testid preserved · every className and inline
 * style preserved · same portal target (document.body) and same open gate.
 *
 * Extracted 2026-09-01 · IJ-460 · Phase 5B of the KnowledgeGraphPage.jsx split
 * (see /app/memory/design_drafts/kg_page_split_map.md · the register 5B name
 * KgGraphCanvas was retired because no code maps to it; this slice extracts
 * the real self-contained modal instead). Owner: E1.
 *
 * Props:
 *   - showDemoIntro · boolean · open gate, parent-owned.
 *   - setShowDemoIntro · function · Cancel button handler, parent-owned.
 *   - handleRunDemo · function · Run-the-Demo button handler, parent-owned.
 */
import React from 'react';
import { createPortal } from 'react-dom';

export const DemoIntroModal = ({ showDemoIntro, setShowDemoIntro, handleRunDemo }) => {
  if (!showDemoIntro) return null;
  return createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="pl-demo-intro-modal">
              <div
                className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                style={{
                  background: 'linear-gradient(135deg, rgba(120,125,140,0.95) 0%, rgba(60,70,90,0.95) 100%)',
                  border: '1px solid rgba(34,211,238,0.4)',
                }}
              >
                <div className="p-6 sm:p-8">
                  <span className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">Knowledge Graph · P&amp;L Demo</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-cyan-400 mt-1 mb-5 leading-snug">
                    Your Knowledge Graph is a knowledge library that allows you to build complex automation, build tools, documents, dashboards and reports.
                  </h2>

                  <p className="text-gray-100 leading-relaxed mb-4">
                    We're providing a real, working example of how the Knowledge Graph and Search.Inc's proprietary design save time, reduce cost, and improve output quality. <span className="font-semibold text-white">This is not a video — it's a live artefact.</span>
                  </p>
                  <p className="text-gray-100 leading-relaxed mb-5">
                    The example demonstrates the creation of a <span className="font-semibold text-cyan-300">2026 Profit &amp; Loss forecast</span> from a limited, fragmented dataset. Inputs include base figures, inconsistent date formats and ranges, and one month of actuals already recorded.
                  </p>

                  {/* Source Excel preview */}
                  <div className="mb-5">
                    <p className="text-xs uppercase tracking-wider text-cyan-300 font-semibold mb-2">The source spreadsheet you're handing the AI</p>
                    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(34,211,238,0.25)' }}>
                      <img
                        src="/icons/budget-source-preview.png"
                        alt="2026 Budget Allocation source spreadsheet"
                        className="w-full block"
                        data-testid="demo-source-preview-img"
                      />
                    </div>
                  </div>

                  <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: '1px solid rgba(34,211,238,0.2)' }}>
                    <p className="text-xs uppercase tracking-wider text-cyan-300 font-semibold mb-2">In the next ~30 seconds the AI will</p>
                    <ul className="space-y-1.5 text-sm text-gray-100">
                      <li>• Read the spreadsheet and detect every cost type</li>
                      <li>• Extract entities — vendors, employees, projects, BAU costs</li>
                      <li>• Build relationships — what's monthly, what's time-bounded, what totals to what</li>
                      <li>• Forecast the full year against the budget</li>
                      <li>• Surface variances, surplus, and what matters</li>
                    </ul>
                  </div>

                  <p className="text-xs text-gray-300 italic mb-5">
                    Once it runs, you'll be able to download the result as Excel.
                  </p>

                  <div className="flex flex-wrap items-center justify-end gap-3">
                    <button
                      onClick={() => setShowDemoIntro(false)}
                      data-testid="pl-demo-cancel-btn"
                      className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRunDemo}
                      data-testid="pl-demo-run-btn"
                      className="px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-lg transition-colors text-sm"
                    >
                      Run the Demo →
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
  );
};

export default DemoIntroModal;
